import { db, get, getAll, mutate, put, setMeta } from "./idb.ts";
import { enqueue, pending, rejected as rejectedList } from "./outbox.ts";
import type {
  BathingGroup, Comment, FuelLoad, HeatSession, Measurement, Position, Rating, Reading,
  StoveEvent,
} from "../types.generated.ts";

const uuid = () => crypto.randomUUID();
const now = () => new Date().toISOString();

/**
 * The only place the app writes. Every method does the same two things in
 * the same order: apply locally, then append to the outbox. The UI reads
 * back from the local store immediately and never learns whether a network
 * existed.
 */

export async function startSession(input: {
  sauna_id: string; stove_id: string; created_by: string | null;
  fire_start_at: string | null;
  /** An evening entered from memory. Without this the database refuses a
      session that has no fire time, which is most of them (FR-8.11). */
  backfilled?: boolean;
}): Promise<HeatSession> {
  const id = uuid();
  const { backfilled = false, ...rest } = input;
  const row = {
    id, ...rest, status: "HEATING", auto_closed: false, backfilled,
    client_id: id, created_at: now(), updated_at: now(),
  } as unknown as HeatSession;
  await put("heat_session", row);
  await enqueue({ kind: "insert_session", row: row as unknown as Record<string, unknown> });
  return row;
}

/**
 * A scalar on the session. `changed_at` is stamped here, on the device that
 * made the change — not when it eventually reaches the server. Two phones
 * editing different fields both keep their value; editing the same field,
 * the later edit wins whichever syncs first (FR-11.7).
 */
export async function patchSession(
  session_id: string, field: string, value: unknown,
): Promise<void> {
  const changed_at = now();
  let existed = false;
  await mutate<HeatSession>("heat_session", session_id, (row) => {
    if (!row) return undefined;
    existed = true;
    return { ...row, [field]: value, updated_at: changed_at };
  });
  if (!existed) throw new Error("no such session");
  await enqueue({ kind: "patch_session", session_id, field, value, changed_at });
}

/**
 * Remove an evening from the archive. A soft delete: the row stays, the
 * audit log records who and when, and setting deleted_at back to null brings
 * it back. Nothing is destroyed by a mis-tap.
 */
export async function deleteSession(session_id: string): Promise<void> {
  await patchSession(session_id, "deleted_at", new Date().toISOString());
}

/** A round of readings. Append-only: rounds never conflict (FR-11.7). */
export async function recordRound(input: {
  session_id: string; recorded_by: string | null;
  values: { position_id: string; temperature_c: number | null; skipped?: boolean;
            confirmed_despite_query?: boolean }[];
  measured_at?: string; note?: string;
}): Promise<Measurement> {
  const id = uuid();
  const measured_at = input.measured_at ?? now();
  const m = {
    id, session_id: input.session_id, measured_at, recorded_by: input.recorded_by,
    edited: false, client_id: id, created_at: now(), note: input.note ?? null,
  } as unknown as Measurement;
  const readings = input.values.map((v) => ({
    id: uuid(), measurement_id: id, position_id: v.position_id,
    temperature_c: v.temperature_c, skipped: v.skipped ?? false,
    source: "MANUAL", confirmed_despite_query: v.confirmed_despite_query ?? false,
  })) as unknown as Reading[];

  await put("measurement", m);
  for (const r of readings) await put("reading", r);
  await enqueue({
    kind: "insert_measurement",
    row: m as unknown as Record<string, unknown>,
    readings: readings as unknown as Record<string, unknown>[],
  });
  return m;
}

/** FR-6.8: a corrected reading is a correction, not a new observation. */
export async function updateRound(measurement_id: string, values: {
  position_id: string; temperature_c: number | null; skipped?: boolean;
}[]): Promise<void> {
  const m = await get<Measurement>("measurement", measurement_id);
  if (!m) throw new Error("no such round");
  await put("measurement", { ...m, edited: true });
  const existing = (await getAll<Reading>("reading"))
    .filter((r) => r.measurement_id === measurement_id);
  const rows = values.map((v) => {
    const prev = existing.find((r) => r.position_id === v.position_id);
    return {
      id: prev?.id ?? uuid(), measurement_id, position_id: v.position_id,
      temperature_c: v.temperature_c, skipped: v.skipped ?? false,
      source: "MANUAL", confirmed_despite_query: prev?.confirmed_despite_query ?? false,
    } as unknown as Reading;
  });
  for (const r of rows) await put("reading", r);
  await enqueue({
    kind: "update_readings", measurement_id,
    readings: rows as unknown as Record<string, unknown>[],
  });
}

/**
 * The stove's layout, and the owner's own starting targets (FR-7.8a). One
 * write covers adding a point, moving it, retiring it and setting its seed —
 * they are all the same row, and treating them separately only invites the
 * four of them to disagree.
 */
export async function savePosition(row: Position): Promise<void> {
  await put("position", row);
  await enqueue({ kind: "upsert_position", row: row as unknown as Record<string, unknown> });
}

/**
 * The sauna's location (FR-10.1a). Typed by the owner, or taken from the
 * device only when they press the button that says so. It is stored once —
 * a sauna does not move — and it exists for one purpose: asking a weather
 * service what it was like outside when the fire was lit.
 */
export async function setSaunaLocation(
  sauna_id: string, lat: number | null, lon: number | null, label?: string | null,
): Promise<void> {
  const row = await get<Record<string, unknown>>("sauna", sauna_id);
  if (!row) return;
  const patch: Record<string, unknown> = {
    location_lat: lat, location_lon: lon,
    ...(label !== undefined ? { location_label: label } : {}),
  };
  await put("sauna", { ...row, ...patch } as { id: string });
  await enqueue({ kind: "update_sauna", id: sauna_id, patch });
}

/** The sauna's own name. Shown in the top bar on every device. */
export async function setSaunaName(sauna_id: string, name: string): Promise<void> {
  const clean = name.trim().slice(0, 120);
  if (!clean) return;
  await mutate<{ id: string; name: string }>("sauna", sauna_id,
    (row) => row && ({ ...row, name: clean }));
  await enqueue({ kind: "update_sauna", id: sauna_id, patch: { name: clean } });
}

/** FR-10.8: new stones. A dated marker, and nothing else follows from it. */
export async function addStoveEvent(input: {
  stove_id: string; type: "STONES" | "REBUILD"; occurred_at: string;
  recorded_by: string | null; note?: string;
}): Promise<StoveEvent> {
  const id = uuid();
  const row = { id, ...input, note: input.note ?? null,
                created_at: now() } as unknown as StoveEvent;
  await put("stove_event", row);
  await enqueue({ kind: "insert_stove_event", row: row as unknown as Record<string, unknown> });
  return row;
}

/**
 * `started_at` is passed in, not taken here. The user picks the time in the
 * prompt — "an hour ago", a typed clock time — and this used to record now()
 * instead, so the device and the server disagreed about when the evening
 * happened. In an application whose entire purpose is recorded times, that is
 * the worst kind of quiet wrong.
 */
/**
 * The session's bathing window, recomputed from its groups.
 *
 * The server derives these two columns with a trigger; the device had no
 * equivalent, so it set them on the screen only. They survived until the
 * next reload and then vanished — an evening that had plainly ended showed
 * no ending. Same rule on both sides: the first group's start, and the last
 * group's end only once none is still open.
 *
 * Not queued for the server: its trigger already does this, from the same
 * rows. Sending it would be telling it something it just worked out.
 */
async function syncBathingWindow(session_id: string): Promise<void> {
  const groups = (await getAll<BathingGroup>("bathing_group"))
    .filter((g) => g.session_id === session_id);
  const starts = groups.map((g) => g.started_at).filter(Boolean).sort();
  const open = groups.some((g) => !g.ended_at);
  const ends = groups.map((g) => g.ended_at).filter(Boolean).sort() as string[];

  await mutate<HeatSession>("heat_session", session_id, (row) => row && ({
    ...row,
    bathing_start_at: starts[0] ?? null,
    bathing_end_at: open ? null : (ends[ends.length - 1] ?? null),
  }));
}

export async function startGroup(
  session_id: string, person_count?: number | null, started_at?: string,
): Promise<BathingGroup> {
  const groups = (await getAll<BathingGroup>("bathing_group"))
    .filter((g) => g.session_id === session_id);
  const id = uuid();
  const row = {
    id, session_id, ordinal: groups.length + 1, started_at: started_at ?? now(),
    ended_at: null, person_count: person_count ?? null, auto_closed: false, client_id: id,
  } as unknown as BathingGroup;
  await put("bathing_group", row);
  await syncBathingWindow(session_id);
  await enqueue({ kind: "insert_group", row: row as unknown as Record<string, unknown> });
  return row;
}

export async function endGroup(id: string, at?: string): Promise<void> {
  const g = await get<BathingGroup>("bathing_group", id);
  /* Returning quietly here is how an evening was lost: the caller passed an
     id it did not have, this did nothing, and the screen showed the group as
     ended anyway. A write that cannot be performed has to be visible. */
  if (!g) throw new Error(`ryhmää ei löydy laitteelta (${id})`);
  const ended_at = at ?? now();
  await put("bathing_group", { ...g, ended_at });
  await syncBathingWindow(g.session_id);
  await enqueue({ kind: "patch_group", id, ended_at });
}

export async function addFuelLoad(
  session_id: string, recorded_by: string | null, loaded_at?: string,
): Promise<FuelLoad> {
  const id = uuid();
  const row = { id, session_id, loaded_at: loaded_at ?? now(), recorded_by, client_id: id,
                note: null } as unknown as FuelLoad;
  await put("fuel_load", row);
  await enqueue({ kind: "insert_fuel", row: row as unknown as Record<string, unknown> });
  return row;
}

export async function rate(input: {
  session_id: string; user_id: string | null; author_name: string; score: number; note?: string;
}): Promise<Rating> {
  const existing = (await getAll<Rating>("rating"))
    .find((r) => r.session_id === input.session_id && r.user_id === input.user_id);
  const id = existing?.id ?? uuid();
  const row = {
    ...existing, id, session_id: input.session_id, user_id: input.user_id,
    author_name: input.author_name, score: input.score, note: input.note ?? null,
    client_id: existing?.client_id ?? id,
    created_at: existing?.created_at ?? now(), updated_at: now(),
  } as unknown as Rating;
  await put("rating", row);
  await enqueue({ kind: "upsert_rating", row: row as unknown as Record<string, unknown> });
  return row;
}

export async function addComment(input: {
  session_id: string; user_id: string | null; author_name: string; body: string;
}): Promise<Comment> {
  const id = uuid();
  const row = { id, ...input, client_id: id, created_at: now(),
                edited_at: null, deleted_at: null } as unknown as Comment;
  await put("comment", row);
  await enqueue({ kind: "insert_comment", row: row as unknown as Record<string, unknown> });
  return row;
}

/**
 * Signing in after having already recorded something.
 *
 * Rows written with no account carry no author: the columns are nullable so
 * that the app works before anyone signs in (FR-11.13). But the server's
 * insert policies require `user_id = auth.uid()`, so those rows could never
 * be pushed — the queue would stop at the first one and stay stopped. They
 * were this person's rows all along; signing in is when they get a name.
 *
 * Both the stored rows and the writes already queued have to be updated:
 * a queued write carries its own copy of the row, and that copy is what the
 * server would see.
 */
export async function adoptLocalRows(user_id: string, display_name: string): Promise<number> {
  const d = await db();
  let touched = 0;

  const AUTHOR: Record<string, string> = {
    heat_session: "created_by", measurement: "recorded_by", fuel_load: "recorded_by",
    stove_event: "recorded_by", rating: "user_id", comment: "user_id",
  };

  for (const [store, field] of Object.entries(AUTHOR)) {
    for (const row of (await d.getAll(store)) as Record<string, unknown>[]) {
      if (row[field] != null) continue;
      row[field] = user_id;
      if ("author_name" in row) row["author_name"] = display_name;
      await d.put(store, row);
      touched += 1;
    }
  }

  for (const entry of await pending()) {
    const op = entry.op;
    let changed = false;
    const fix = (row: Record<string, unknown>, field: string) => {
      if (row[field] == null) { row[field] = user_id; changed = true; }
      if ("author_name" in row && changed) row["author_name"] = display_name;
    };
    if (op.kind === "insert_session") fix(op.row, "created_by");
    else if (op.kind === "insert_measurement") fix(op.row, "recorded_by");
    else if (op.kind === "insert_fuel") fix(op.row, "recorded_by");
    else if (op.kind === "insert_stove_event") fix(op.row, "recorded_by");
    else if (op.kind === "upsert_rating") fix(op.row, "user_id");
    else if (op.kind === "insert_comment") fix(op.row, "user_id");
    if (changed) { await d.put("outbox", entry); touched += 1; }
  }
  return touched;
}

/**
 * A one-time repair for rows written before `backfilled` was set on them.
 *
 * A session with no fire time is a backfilled one by definition — that is the
 * only way to have one. Earlier builds wrote those with backfilled = false,
 * which the database refuses outright, so they sat at the head of the queue
 * holding everything behind them. Rewriting the flag is not a guess about
 * what the user meant; it is the only value the row can have.
 */
export async function repairBackfilledFlag(): Promise<number> {
  const d = await db();
  let fixed = 0;

  for (const row of (await d.getAll("heat_session")) as Record<string, unknown>[]) {
    if (row["fire_start_at"] == null && row["backfilled"] !== true) {
      row["backfilled"] = true;
      await d.put("heat_session", row);
      fixed += 1;
    }
  }
  // Both the queue and the writes already set aside: a row parked because
  // the server refused it is exactly the one this repair is for.
  for (const [store, list] of [
    ["outbox", await pending()], ["rejected", await rejectedList()],
  ] as const) {
    for (const entry of list) {
      const op = entry.op;
      if (op.kind !== "insert_session") continue;
      if (op.row["fire_start_at"] == null && op.row["backfilled"] !== true) {
        op.row["backfilled"] = true;
        await d.put(store, entry);
        fixed += 1;
      }
    }
  }
  return fixed;
}

// ---- reads -----------------------------------------------------------

export async function sessionsOf(sauna_id: string): Promise<HeatSession[]> {
  return (await getAll<HeatSession>("heat_session"))
    .filter((s) => s.sauna_id === sauna_id && !s.deleted_at)
    .sort((a, b) => String(b.fire_start_at ?? "").localeCompare(String(a.fire_start_at ?? "")));
}

export async function openSessionOf(sauna_id: string): Promise<HeatSession | undefined> {
  return (await sessionsOf(sauna_id))
    .find((s) => ["DRAFT", "HEATING", "VENTING", "BATHING"].includes(s.status));
}

export async function roundsOf(session_id: string) {
  const ms = (await getAll<Measurement>("measurement"))
    .filter((m) => m.session_id === session_id)
    .sort((a, b) => a.measured_at.localeCompare(b.measured_at));
  const rs = await getAll<Reading>("reading");
  return ms.map((m) => ({ ...m, readings: rs.filter((r) => r.measurement_id === m.id) }));
}

export async function activePositions(stove_id: string): Promise<Position[]> {
  return (await getAll<Position>("position"))
    .filter((p) => p.stove_id === stove_id && !p.retired_at)
    .sort((a, b) => a.sort_order - b.sort_order);
}

export async function markSynced() { await setMeta("last_sync_at", now()); }
